// src/pages/msme/MSMEAdvisorsHome.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes, faDownload, faStar, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import jsPDF from "jspdf";

const dummyAdvisors = [
  { id: 1, name: "Rajesh Kumar", expertise: "Process Management & Transformation", rating: 4.5, myAdvisor: true },
  { id: 2, name: "Priya Sharma", expertise: "Process Management ", rating: 4.0, myAdvisor: false },
  { id: 3, name: "Ramesh Gupta", expertise: "Process Management ", rating: 3.8, myAdvisor: false },
  { id: 4, name: "Meena Joshi", expertise: "Transformation", rating: 4.2, myAdvisor: false },
];

const inputClass = "px-4 py-3 rounded-lg bg-gray-800 text-gray-100 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-full text-sm";
const buttonClass = "px-4 py-3 rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2 min-h-[44px]";

const MSMEAdvisorsHome = () => {
  const [advisors, setAdvisors] = useState(dummyAdvisors);
  const [showAdvisorForm, setShowAdvisorForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [advisorForm, setAdvisorForm] = useState({ name: "", expertise: "", email: "", phone: "", experience: "" });
  const [requestForm, setRequestForm] = useState({ company: "", contactPerson: "", email: "", phone: "", requirements: "" });
  const [notifications, setNotifications] = useState([]);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [showAdvisorDetails, setShowAdvisorDetails] = useState(false);

  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 5000);
  };

  const handleAddAdvisor = (e) => {
    e.preventDefault();
    if (!advisorForm.name || !advisorForm.expertise) {
      addNotification("Please fill required fields!", "error");
      return;
    }
    const newAdvisor = { ...advisorForm, id: Date.now(), rating: 0, myAdvisor: false };
    setAdvisors(prev => [...prev, newAdvisor]);
    setShowAdvisorForm(false);
    setAdvisorForm({ name: "", expertise: "", email: "", phone: "", experience: "" });
    addNotification("Advisor application submitted!", "success");
  };

  const handleRequestAdvisor = (e) => {
    e.preventDefault();
    if (!requestForm.company || !requestForm.contactPerson) {
      addNotification("Please fill required fields!", "error");
      return;
    }
    setShowRequestForm(false);
    setRequestForm({ company: "", contactPerson: "", email: "", phone: "", requirements: "" });
    addNotification("Advisor request submitted!", "success");
  };

  const handlePDFExport = () => {
    const doc = new jsPDF();
    doc.text("SAKSHAM Advisors List", 10, 10);
    let y = 20;
    advisors.forEach((a, idx) => {
      doc.text(`${idx + 1}. ${a.name} - ${a.expertise} - Rating: ${a.rating}`, 10, y);
      y += 10;
    });
    doc.save("advisors_list.pdf");
    addNotification("PDF exported!", "success");
  };

  const sortedAdvisors = [...advisors].sort((a, b) => b.myAdvisor - a.myAdvisor);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-4">
      {/* Header */}
      <header className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-white">SAKSHAM MSME Certified Advisors</h1>
      </header>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6 items-center">
        <button
          onClick={() => setShowAdvisorForm(true)}
          className={`${buttonClass} bg-emerald-600 hover:bg-emerald-700 text-white flex-1`}
        >
          <FontAwesomeIcon icon={faPlus} /> Apply as Advisor
        </button>
        <button
          onClick={() => setShowRequestForm(true)}
          className={`${buttonClass} bg-indigo-600 hover:bg-indigo-700 text-white flex-1`}
        >
          <FontAwesomeIcon icon={faEnvelope} /> I Need Advisor
        </button>
        <button
          onClick={handlePDFExport}
          className={`${buttonClass} bg-gray-600 hover:bg-gray-700 text-white flex-1`}
        >
          <FontAwesomeIcon icon={faDownload} /> Export PDF
        </button>
      </div>

      {/* Advisors List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedAdvisors.map(a => (
          <div
            key={a.id}
            onClick={() => { setSelectedAdvisor(a); setShowAdvisorDetails(true); }}
            className={`p-4 rounded-xl shadow-lg transition-all cursor-pointer ${
              a.myAdvisor ? "bg-emerald-700 border-2 border-amber-400" : "bg-gray-800"
            } hover:scale-105`}
          >
            <h3 className="text-lg font-semibold text-white">{a.name}</h3>
            <p className="text-gray-300 mb-2">Expertise: {a.expertise}</p>
            <p className="text-yellow-400 flex items-center gap-1">
              {Array.from({ length: Math.floor(a.rating) }).map((_, i) => (
                <FontAwesomeIcon key={i} icon={faStar} />
              ))}
              <span className="text-gray-200 ml-1">{a.rating.toFixed(1)}</span>
            </p>
            {a.myAdvisor && <p className="text-amber-200 font-medium mt-2">My Advisor</p>}
          </div>
        ))}
      </div>

      {/* Advisor Details Modal */}
      {showAdvisorDetails && selectedAdvisor && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setShowAdvisorDetails(false)}
          ></div>
          <div className="relative bg-gray-800 p-6 rounded-xl max-w-md mx-auto mt-12 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">{selectedAdvisor.name}</h3>
              <button
                onClick={() => setShowAdvisorDetails(false)}
                className="text-gray-400 hover:text-gray-200 w-10 text-xl"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="space-y-3 text-gray-200">
              <p><span className="font-semibold">Expertise:</span> {selectedAdvisor.expertise}</p>
              <p><span className="font-semibold">Rating:</span> {selectedAdvisor.rating.toFixed(1)} / 5</p>
              <p><span className="font-semibold">Email:</span> {selectedAdvisor.email || "msmeadvisor@think5whys.com"}</p>
              <p><span className="font-semibold">Phone:</span> {selectedAdvisor.phone || "+91 99999 99999"}</p>
              <p><span className="font-semibold">Experience:</span> {selectedAdvisor.experience || "5 years"}</p>
              <p><span className="font-semibold">Special Notes:</span> Certified in Micro Lean Six Sigma by Think5Whys.</p>
              {selectedAdvisor.myAdvisor && <p className="text-amber-200 font-medium">This is your Advisor</p>}
            </div>
          </div>
        </div>
      )}



      {/* Advisor Application Form Modal */}
      {showAdvisorForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowAdvisorForm(false)}></div>
          <div className="relative bg-gray-800 p-6 rounded-xl max-w-md mx-auto mt-12 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Apply as Advisor</h3>
              <button onClick={() => setShowAdvisorForm(false)} className="text-gray-400 hover:text-gray-200 text-xl">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form className="space-y-4" onSubmit={handleAddAdvisor}>
              <input
                required
                placeholder="Full Name"
                value={advisorForm.name}
                onChange={e => setAdvisorForm({ ...advisorForm, name: e.target.value })}
                className={inputClass}
              />
              <input
                required
                placeholder="Expertise (e.g., Finance, Marketing)"
                value={advisorForm.expertise}
                onChange={e => setAdvisorForm({ ...advisorForm, expertise: e.target.value })}
                className={inputClass}
              />
              <input
                placeholder="Email"
                value={advisorForm.email}
                onChange={e => setAdvisorForm({ ...advisorForm, email: e.target.value })}
                className={inputClass}
              />
              <input
                placeholder="Phone"
                value={advisorForm.phone}
                onChange={e => setAdvisorForm({ ...advisorForm, phone: e.target.value })}
                className={inputClass}
              />
              <input
                placeholder="Experience (years)"
                value={advisorForm.experience}
                onChange={e => setAdvisorForm({ ...advisorForm, experience: e.target.value })}
                className={inputClass}
              />
              <button type="submit" className={`${buttonClass} w-full bg-emerald-600 hover:bg-emerald-700 text-white`}>
                Submit Application
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Advisor Request Form Modal */}
      {showRequestForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowRequestForm(false)}></div>
          <div className="relative bg-gray-800 p-6 rounded-xl max-w-md mx-auto mt-12 shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-white">Request an Advisor</h3>
              <button onClick={() => setShowRequestForm(false)} className="text-gray-400 hover:text-gray-200 text-xl">
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form className="space-y-4" onSubmit={handleRequestAdvisor}>
              <input
                required
                placeholder="Company Name"
                value={requestForm.company}
                onChange={e => setRequestForm({ ...requestForm, company: e.target.value })}
                className={inputClass}
              />
              <input
                required
                placeholder="Contact Person"
                value={requestForm.contactPerson}
                onChange={e => setRequestForm({ ...requestForm, contactPerson: e.target.value })}
                className={inputClass}
              />
              <input
                placeholder="Email"
                value={requestForm.email}
                onChange={e => setRequestForm({ ...requestForm, email: e.target.value })}
                className={inputClass}
              />
              <input
                placeholder="Phone"
                value={requestForm.phone}
                onChange={e => setRequestForm({ ...requestForm, phone: e.target.value })}
                className={inputClass}
              />
              <textarea
                placeholder="Advisor Requirements / Notes"
                value={requestForm.requirements}
                onChange={e => setRequestForm({ ...requestForm, requirements: e.target.value })}
                rows={3}
                className={`${inputClass} resize-none`}
              />
              <button type="submit" className={`${buttonClass} w-full bg-indigo-600 hover:bg-indigo-700 text-white`}>
                Submit Request
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Notifications */}
      {notifications.map(n => (
        <div
          key={n.id}
          className={`fixed top-4 right-4 p-4 rounded-xl shadow-2xl z-50 text-white max-w-sm ${
            n.type === "success" ? "bg-emerald-600" : n.type === "error" ? "bg-red-500" : "bg-indigo-600"
          } animate-slide-in-right`}
        >
          <div className="flex justify-between items-start">
            <span className="flex-1 text-base">{n.message}</span>
            <button onClick={() => setNotifications(prev => prev.filter(x => x.id !== n.id))} className="ml-4 text-white hover:text-gray-200 text-xl">
              ×
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MSMEAdvisorsHome;
